// Shader noise that repeats every WORLD_W in x, so the east-west wrap never shows a seam.
//
// The camera x is kept inside [0, WORLD_W) and chunks are drawn shifted by ±WORLD_W, so the same
// ground is rendered at x and at x ± WORLD_W. Any noise read from world x must agree at both, which
// value noise does when its lattice wraps: cell ix and ix + period hash to the same value.
//
//     x ─► x · period / WORLD_W ─► floor ─► mod(period) ─► hash
//
// `period` is the number of lattice cells across the world and must be an integer.

import { Fn, dot, float, floor, fract, mix, mod, vec2, vec3 } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { WORLD_W } from '../../core/grid.ts';

type F = Node<'float'>;
type V2 = Node<'vec2'>;

// "Hash without sine" (Dave Hoskins): stable in float32 for lattice coordinates up to ~1e4.
const HASH_SCALE = 0.1031;
const HASH_SHIFT = 33.33;

// crossNoise: shear of each lattice and the frequency ratio between them.
const CROSS_SHEAR = 0.58;
const CROSS_RATIO = 1.3;

/** Hash of a lattice point to [0, 1). */
export const hash12 = Fn(([p]: [V2]) => {
  const p3 = fract(vec3(p.x, p.y, p.x).mul(HASH_SCALE)).toVar();
  p3.addAssign(dot(p3, p3.yzx.add(HASH_SHIFT)));
  return fract(p3.x.add(p3.y).mul(p3.z));
}).setLayout({ name: 'wc_hash12', type: 'float', inputs: [{ name: 'p', type: 'vec2' }] });

/** Value noise in [0, 1), periodic in x with an integer `period` of lattice cells. */
export const vnoiseP = Fn(([p, period]: [V2, F]) => {
  const i = floor(p);
  const f = fract(p);
  const u = f.mul(f).mul(f.mul(-2).add(3));
  const x0 = mod(i.x, period);
  const x1 = mod(i.x.add(1), period);
  const a = hash12(vec2(x0, i.y));
  const b = hash12(vec2(x1, i.y));
  const c = hash12(vec2(x0, i.y.add(1)));
  const d = hash12(vec2(x1, i.y.add(1)));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}).setLayout({ name: 'wc_vnoiseP', type: 'float', inputs: [{ name: 'p', type: 'vec2' }, { name: 'period', type: 'float' }] });

/** Lattice cells across the world for a wanted frequency (cells per world unit). */
export function periodFor(freq: number): number {
  return Math.max(1, Math.round(freq * WORLD_W));
}

/**
 * Seamless value noise at world XZ (x already wrapped or not: the lattice wraps either way).
 * `freq` is cells per world unit; the real frequency is rounded so the world holds whole cells.
 */
export function worldNoise(xz: V2, freq: number): F {
  const period = periodFor(freq);
  const scale = period / WORLD_W;
  return vnoiseP(xz.mul(scale), float(period));
}

/**
 * worldNoise on a lattice sheared along x (x + shear·z, z). A shear along x keeps the east-west
 * period, and summing two opposite shears hides the square lattice of plain value noise.
 */
function shearNoise(xz: V2, freq: number, shear: number): F {
  return worldNoise(vec2(xz.x.add(xz.y.mul(shear)), xz.y), freq);
}

/** Two opposite shears blended: isotropic-looking value noise, still seamless. */
export function crossNoise(xz: V2, freq: number): F {
  const a = shearNoise(xz, freq, CROSS_SHEAR);
  const b = shearNoise(xz.add(vec2(0, 3.1)), freq * CROSS_RATIO, -CROSS_SHEAR);
  return a.mul(0.6).add(b.mul(0.4));
}

/** Three-octave fbm of worldNoise, normalised to [0, 1). */
export function worldFbm(xz: V2, freq: number): F {
  const a = worldNoise(xz, freq);
  const b = worldNoise(xz.add(vec2(0, 17.1)), freq * 2);
  const c = worldNoise(xz.add(vec2(0, 3.7)), freq * 4);
  return a.mul(0.5).add(b.mul(0.25)).add(c.mul(0.125)).div(0.875);
}

// Brush-stroke frame: long along x, short across, with a shear so strokes lean diagonally.
const STROKE_ALONG = 0.35;
const STROKE_ACROSS = 1.6;
const STROKE_SHEAR = 0.45;
const STROKE_LEVELS = 5;
const STROKE_QUANT = 0.7;

/**
 * Brush strokes (port of oneshot-rts shading.ts `strokes`): noise stretched along one direction and
 * partly quantised into flat painted patches. Periodic in x like worldNoise.
 */
export function strokes(xz: V2, freq: number): F {
  const periodU = periodFor(freq * STROKE_ALONG);
  const su = periodU / WORLD_W;
  const u = xz.x.mul(su).add(xz.y.mul(su * STROKE_SHEAR));
  const v = xz.y.mul(freq * STROKE_ACROSS);
  const p = vec2(u, v);
  const n = vnoiseP(p, float(periodU)).mul(0.5)
    .add(vnoiseP(p.mul(2).add(vec2(0, 17.1)), float(periodU * 2)).mul(0.25))
    .add(vnoiseP(p.mul(4).add(vec2(0, 3.7)), float(periodU * 4)).mul(0.125))
    .div(0.875);
  const q = floor(n.mul(STROKE_LEVELS).add(0.5)).div(STROKE_LEVELS);
  return mix(n, q, STROKE_QUANT);
}

/** Jittered-cell (Worley) distances F1, F2 at p, periodic in x with `period` cells. */
export const cellsP = Fn(([p, period]: [V2, F]) => {
  const i = floor(p);
  const f = fract(p);
  const f1 = float(8).toVar();
  const f2 = float(8).toVar();
  const id = float(0).toVar();
  for (let dz = -1; dz <= 1; dz++) {
    for (let dx = -1; dx <= 1; dx++) {
      const cell = vec2(mod(i.x.add(dx), period), i.y.add(dz));
      const jitter = vec2(hash12(cell), hash12(cell.add(vec2(0, 57.3))));
      const d = vec2(dx, dz).add(jitter).sub(f).length();
      const closer = d.lessThan(f1);
      f2.assign(closer.select(f1, d.min(f2)));
      id.assign(closer.select(hash12(cell.add(vec2(31.7, 0))), id));
      f1.assign(d.min(f1));
    }
  }
  return vec3(f1, f2, id);
}).setLayout({ name: 'wc_cellsP', type: 'vec3', inputs: [{ name: 'p', type: 'vec2' }, { name: 'period', type: 'float' }] });

/** Worley cells at world XZ: (F1, F2, cell id in [0, 1)). */
export function worldCells(xz: V2, freq: number): Node<'vec3'> {
  const period = periodFor(freq);
  return cellsP(xz.mul(period / WORLD_W), float(period));
}
